import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { QrCode } from 'lucide-react';
import { errorMessage, post } from '../../lib/api';
import { Spinner } from '../../ui/primitives';
import { RestaurantAvatar } from '../RestaurantBadge';
import type { TenantRestaurant } from '../TenantApp';
import { publicPath } from '../../lib/paths';

/**
 * Destino do QR Code impresso na mesa: /r/{slug}/mesa/{numero}?qr={token}.
 * Entra na comanda e leva ao cardápio. O número só deixa o endereço legível: quem identifica a mesa é o
 * token do QR Code (sem ele, alguém fora do restaurante poderia entrar em qualquer mesa digitando o número).
 */
export function JoinTable({ restaurant }: { restaurant: TenantRestaurant }) {
  const { mesa = '' } = useParams();
  const [search] = useSearchParams();
  const token = search.get('qr') ?? '';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string>();
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!token) {
      setError(`Para pedir na mesa ${mesa}, escaneie o QR Code que está nela.`);
      return;
    }
    post<{ tableLabel: string }>(`/api/public/tables/${encodeURIComponent(token)}/join`)
      .then(async (r) => {
        await qc.invalidateQueries({ queryKey: ['guest-session'] });
        navigate(publicPath(), { replace: true, state: { joined: r.tableLabel } });
      })
      .catch((e) => setError(errorMessage(e)));
  }, [token, mesa, navigate, qc]);

  if (!error) return <Spinner label="Identificando sua mesa…" />;
  return (
    <main className="grid min-h-dvh place-items-center px-4 text-center">
      <div className="max-w-sm">
        <RestaurantAvatar r={restaurant} className="mx-auto size-16" />
        <div className="mx-auto mt-6 grid size-12 place-items-center rounded-2xl bg-paper-2 text-ink-3"><QrCode className="size-6" /></div>
        <h1 className="mt-3 text-lg font-semibold">Não conseguimos identificar a mesa</h1>
        <p className="mt-1 text-sm text-ink-3">{error}</p>
        <Link to={publicPath()} className="mt-6 inline-block text-sm font-medium text-[var(--brand)] underline">Ver o cardápio mesmo assim</Link>
      </div>
    </main>
  );
}
